import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { supabase } from '../supabaseClient';
import { Heading, Text, Button } from '../styles/components';
import AssessmentProcessing from './AssessmentProcessing';

const FormContainer = styled.div`
  background-color: white;
  width: 100%;
  max-width: 600px;
  border-radius: ${props => props.theme.radii.lg};
  padding: ${props => props.theme.spacing.xl};
  position: relative;
  display: flex;
  flex-direction: column;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
  border: 1px solid ${props => props.theme.colors.border};
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.lg};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
  margin-top: ${props => props.theme.spacing.lg};
`;

// Floating Label Components
const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: 5px; /* Space for label to float up */
`;

const FloatingLabel = styled.label`
  position: absolute;
  top: 14px;
  left: 14px;
  font-size: 1rem;
  color: ${props => props.theme.colors.textLight};
  transition: all 0.2s ease-in-out;
  pointer-events: none;
  background: white;
  padding: 0 4px;
  z-index: 1;

  ${props => (props.$active || props.$hasValue) && css`
    top: -10px;
    left: 10px;
    font-size: 0.8rem;
    color: ${props.theme.colors.terracotta};
    font-weight: 600;
  `}
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 14px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.sm};
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: 1rem;
  background-color: transparent; /* allow label bg to cover border */
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.terracotta};
    box-shadow: 0 0 0 2px rgba(200, 85, 61, 0.1);
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 14px;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.radii.sm};
  font-family: ${props => props.theme.typography.body.fontFamily};
  font-size: 1rem;
  background-color: white;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.terracotta};
    box-shadow: 0 0 0 2px rgba(200, 85, 61, 0.1);
  }
  
  /* Custom arrow */
  background-image: linear-gradient(45deg, transparent 50%, gray 50%),
                    linear-gradient(135deg, gray 50%, transparent 50%);
  background-position: calc(100% - 20px) calc(1em + 2px),
                       calc(100% - 15px) calc(1em + 2px);
  background-size: 5px 5px,
                   5px 5px;
  background-repeat: no-repeat;
`;


// Drag & Drop Upload
const DropZone = styled.div`
  border: 2px dashed ${props => props.$isDragActive ? props.theme.colors.terracotta : props.$hasFile ? props.theme.colors.sage : props.theme.colors.border};
  border-radius: ${props => props.theme.radii.md};
  padding: ${props => props.theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${props => props.$isDragActive ? 'rgba(200, 85, 61, 0.05)' : props.$hasFile ? 'rgba(120, 168, 131, 0.1)' : props.theme.colors.bone};
  position: relative;
  
  &:hover {
    border-color: ${props => props.$hasFile ? props.theme.colors.sage : props.theme.colors.terracotta};
    background-color: ${props => props.$hasFile ? 'rgba(120, 168, 131, 0.15)' : 'rgba(200, 85, 61, 0.05)'};
  }

  input {
    display: none;
  }
`;

const FileInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: ${props => props.theme.colors.charcoal};
    font-weight: 500;
`;


const ErrorMessage = styled.div`
  color: red;
  font-size: ${props => props.theme.typography.sizes.bodySmall};
  margin-top: ${props => props.theme.spacing.sm};
  text-align: center;
`;

const STAGES = [
    'Pre-seed',
    'Seed',
    'Series A',
    'Series B',
    'Series C',
    'Crossover',
    'Extension'
];

const STATUSES = [
    'Currently raising',
    'Preparing to raise',
    'Already raised (post-mortem)'
];

const AssessmentForm = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('form'); // form, processing
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        stage: '',
        status: '',
        file: null,
        marketingConsent: false
    });
    const [error, setError] = useState('');
    const [activeField, setActiveField] = useState(null); // For floating status tracking
    const [isDragActive, setIsDragActive] = useState(false);

    const fileInputRef = useRef(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        validateAndSetFile(file);
    };

    const validateAndSetFile = (file) => {
        if (file && file.type === 'application/pdf') {
            setFormData(prev => ({ ...prev, file }));
            setError('');
        } else if (file) {
            setError('Please upload a valid PDF file.');
        }
    };

    // Drag & Drop Handlers
    const onDragOver = (e) => {
        e.preventDefault();
        setIsDragActive(true);
    };

    const onDragLeave = (e) => {
        e.preventDefault();
        setIsDragActive(false);
    };

    const onDrop = (e) => {
        e.preventDefault();
        setIsDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.file) {
            setError('Please upload a pitch deck (PDF).');
            return;
        }

        if (!supabase) {
            setError('Database connection not available.');
            return;
        }

        setStep('processing');
        setError('');

        try {
            // 1. Upload File
            const fileName = `${Date.now()}_${formData.file.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
            const { data: uploadData, error: uploadError } = await supabase.storage
                .from('decks')
                .upload(fileName, formData.file);

            if (uploadError) throw uploadError;

            // 2. Call Assessment Function
            const { data: analysisData, error: functionError } = await supabase.functions.invoke('assess-deck', {
                body: {
                    name: formData.name,
                    email: formData.email,
                    stage: formData.stage,
                    status: formData.status,
                    deck_path: uploadData.path
                }
            });

            if (functionError) throw functionError;

            // Redirect to results page
            navigate('/assessment-results', {
                state: {
                    results: analysisData,
                    email: formData.email
                }
            });

        } catch (err) {
            console.error('Assessment failed:', err);
            let errorMessage = 'Something went wrong during the assessment. Please try again.';

            // Error handling logic (same as before)
            if (err && typeof err === 'object') {
                if ('context' in err && typeof err.context?.json === 'function') {
                    try {
                        const errorBody = await err.context.json();
                        if (errorBody.error) errorMessage = `Analysis Failed: ${errorBody.error}`;
                    } catch (e) {
                        // ignore
                    }
                } else if (err.message) {
                    errorMessage = err.message || errorMessage;
                }
            }

            setError(errorMessage);
            setStep('form');
        }
    };

    if (step === 'processing') {
        return (
            <FormContainer>
                <AssessmentProcessing />
            </FormContainer>
        );
    }

    return (
        <FormContainer>
            <Heading as="h2" size="h3" $mb="0.5rem" style={{ textAlign: 'center' }}>Assess My Pitch</Heading>
            <Text size="small" style={{ textAlign: 'center', marginBottom: '1rem' }}>
                Upload your deck for a strategic AI review. You'll get an instant overview here and a detailed PDF report via email.
            </Text>

            <Form onSubmit={handleSubmit}>
                <InputWrapper>
                    <StyledInput
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        onFocus={() => setActiveField('name')}
                        onBlur={() => setActiveField(null)}
                        required
                    />
                    <FloatingLabel $active={activeField === 'name'} $hasValue={!!formData.name}>Your Name</FloatingLabel>
                </InputWrapper>

                <InputWrapper>
                    <StyledInput
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        onFocus={() => setActiveField('email')}
                        onBlur={() => setActiveField(null)}
                        required
                    />
                    <FloatingLabel $active={activeField === 'email'} $hasValue={!!formData.email}>Work Email</FloatingLabel>
                </InputWrapper>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                    <InputWrapper>
                        <StyledSelect
                            name="stage"
                            value={formData.stage}
                            onChange={handleInputChange}
                            required
                        >
                            <option value=""></option>
                            {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                        </StyledSelect>
                        <FloatingLabel $active={true} $hasValue={!!formData.stage}>Stage</FloatingLabel>
                    </InputWrapper>

                    <InputWrapper>
                        <StyledSelect
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                            required
                        >
                            <option value=""></option>
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </StyledSelect>
                        <FloatingLabel $active={true} $hasValue={!!formData.status}>Status</FloatingLabel>
                    </InputWrapper>
                </div>

                <DropZone
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                    $isDragActive={isDragActive}
                    $hasFile={!!formData.file}
                >
                    <input
                        type="file"
                        accept=".pdf"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                    />

                    {formData.file ? (
                        <FileInfo>
                            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#78a883' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {formData.file.name}
                        </FileInfo>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ color: '#ccc' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                            </svg>
                            <Text color="charcoal" style={{ margin: 0 }}>Drop PDF deck here</Text>
                            <Text size="small" color="textLight" style={{ margin: 0 }}>or click to browse</Text>
                        </div>
                    )}
                </DropZone>

                <InputWrapper style={{ marginTop: '-10px' }}>
                    <label style={{ fontWeight: 'normal', fontSize: '0.85rem', display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', color: '#666' }}>
                        <input
                            type="checkbox"
                            name="marketingConsent"
                            checked={formData.marketingConsent}
                            onChange={(e) => setFormData(prev => ({ ...prev, marketingConsent: e.target.checked }))}
                            style={{ marginTop: '3px' }}
                        />
                        <span>I share my deck in confidence. I understand it will be processed by AI on secured servers.</span>
                    </label>
                </InputWrapper>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                <Button type="submit" style={{ marginTop: '0.5rem', width: '100%' }}>
                    Get Strategy Audit
                </Button>

                <Text size="small" color="textLight" style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.75rem' }}>
                    🔒 SOC2 Compliant Storage • ⚡ Results in 60 seconds
                </Text>
            </Form>
        </FormContainer>
    );
};

export default AssessmentForm;
